import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Simple notification bell — polls every 60s while mounted, opens a dropdown
// with the last 50 items. Marking one read updates the bell badge live.
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ records: [], unread: 0 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setState({ records: data.records || [], unread: data.unread || 0 });
    } catch { /* silent — bell shouldn't fight for attention */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/notifications/read-all");
      await load();
    } finally { setBusy(false); }
  };

  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); await load(); } catch {}
  };

  const takeOwnership = async (n, endpoint) => {
    try {
      await api.post(`/onboarding/${n.meta.onboarding_id}/${endpoint}`);
      await api.post(`/notifications/${n.id}/read`);
      toast.success("Ownership accepted");
      await load();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to take ownership");
    }
  };

  const declineAssignment = async (n) => {
    const reason = window.prompt("Why are you declining this assignment? (optional)", "");
    if (reason === null) return; // user hit cancel
    try {
      await api.post(`/onboarding/${n.meta.onboarding_id}/decline-assignment`, {
        role: n.meta.role,
        reason,
      });
      await api.post(`/notifications/${n.id}/read`);
      toast.success("Assignment declined");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to decline");
    }
  };

  return (
    <div className="relative z-50" data-testid="notification-bell">
      <Button
        variant="outline" size="sm"
        className="rounded-sm border-[#0B132B]/20 hover:bg-[#0B132B] hover:text-white relative h-9 w-9 p-0 z-50"
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell size={15} />
        {state.unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-black rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
          >
            {state.unread > 9 ? "9+" : state.unread}
          </span>
        )}
      </Button>
      {open && (
        <>
          {/* Backdrop sits BELOW the bell + dropdown so the trigger stays
              clickable to toggle closed and navbar clicks aren't absorbed. */}
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white border border-[#E9ECEF] rounded-sm shadow-lg z-50"
            data-testid="notification-dropdown"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#E9ECEF]">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Notifications</div>
              {state.unread > 0 && (
                <button
                  className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#D4AF37] hover:text-[#0B132B]"
                  onClick={markAll} disabled={busy}
                  data-testid="notification-mark-all"
                >
                  Mark all read
                </button>
              )}
            </div>
            {state.records.length === 0 ? (
              <div className="px-3 py-6 text-sm text-[#6C757D] italic text-center">You're all caught up.</div>
            ) : state.records.map((n) => (
              <NotificationItem
                key={n.id}
                n={n}
                onRead={() => markOne(n.id)}
                onClose={() => setOpen(false)}
                onAction={takeOwnership}
                onDecline={declineAssignment}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NotificationItem({ n, onRead, onClose, onAction, onDecline }) {
  const isAssignment =
    n.type === "onboarding_assigned" &&
    !n.is_read &&
    n.meta?.onboarding_id &&
    (n.meta?.role === "production" || n.meta?.role === "testing" || n.meta?.role === "install");
  const canTakeOwnership = isAssignment && (n.meta?.role === "production" || n.meta?.role === "testing");

  const take = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const endpoint = n.meta.role === "production" ? "take-production" : "take-testing";
    onAction(n, endpoint);
  };

  const decline = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDecline(n);
  };

  const inner = (
    <div
      className={`px-3 py-2 border-b border-[#F1F3F5] hover:bg-[#F8F9FA] cursor-pointer ${n.is_read ? "opacity-60" : ""}`}
      onClick={() => { onRead(); onClose(); }}
      data-testid={`notification-item-${n.id}`}
    >
      <div className="flex items-start gap-2">
        {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-[#0B132B] leading-tight">{n.title}</div>
          {n.body && <div className="text-[11px] text-[#6C757D] mt-0.5 line-clamp-2">{n.body}</div>}
          <div className="flex items-center gap-2 mt-1">
            <div className="text-[10px] text-[#9CA3AF]">{formatTime(n.created_at)}</div>
            {canTakeOwnership && (
              <button
                onClick={take}
                data-testid={`notification-take-ownership-${n.id}`}
                className="text-[9px] uppercase tracking-[0.14em] font-bold bg-[#0B132B] text-white px-2 py-0.5 rounded-sm hover:bg-[#D4AF37] hover:text-[#0B132B] transition-colors"
              >
                Take Ownership
              </button>
            )}
            {isAssignment && (
              <button
                onClick={decline}
                data-testid={`notification-decline-${n.id}`}
                className="text-[9px] uppercase tracking-[0.14em] font-bold bg-white text-red-600 border border-red-300 px-2 py-0.5 rounded-sm hover:bg-red-600 hover:text-white transition-colors"
              >
                Decline
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  return n.link ? <Link to={n.link}>{inner}</Link> : inner;
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}
